/**
 * AI Council provider icon mapping.
 *
 * Maps any provider name, model id, or council role (case-insensitive) to the
 * branded PNG asset for one of the four AI Council members.
 *
 * Council member mapping:
 *   - ChatGPT / GPT / OpenAI                            -> Brain
 *   - Claude / Anthropic                                -> Owl
 *   - Gemini / Google                                   -> Elephant
 *   - Perplexity                                        -> Compass
 *
 * Council role aliases also resolve so back-end "council_members[].role" /
 * agent names route to the right icon without each call site re-implementing
 * the mapping.
 */

export type AICouncilProvider = 'chatgpt' | 'claude' | 'gemini' | 'perplexity' | 'unknown';

export interface AIProviderIcon {
  /** Absolute URL to the PNG asset under public/ai-icons/. */
  src: string | null;
  /** Accessible alt text (or initials for unknown providers). */
  alt: string;
  /** Display-friendly provider label. */
  label: string;
  /** Normalized provider key. */
  provider: AICouncilProvider;
}

const ICON_PATH: Record<Exclude<AICouncilProvider, 'unknown'>, string> = {
  chatgpt:    '/ai-icons/chatgpt-brain.png',
  claude:     '/ai-icons/claude-owl.png',
  gemini:     '/ai-icons/gemini-elephant.png',
  perplexity: '/ai-icons/perplexity-compass.png',
};

const ICON_ALT: Record<Exclude<AICouncilProvider, 'unknown'>, string> = {
  chatgpt:    'ChatGPT Brain icon',
  claude:     'Claude Owl icon',
  gemini:     'Gemini Elephant icon',
  perplexity: 'Perplexity Compass icon',
};

const ICON_LABEL: Record<Exclude<AICouncilProvider, 'unknown'>, string> = {
  chatgpt:    'GPT',
  claude:     'Claude',
  gemini:     'Gemini',
  perplexity: 'Perplexity',
};

/**
 * Normalize a provider name, model id, or council role to one of the four
 * known Council members. Returns 'unknown' if no alias matches.
 *
 * Aliases include vendor names, common model ids, and AI Council role titles
 * (Market Analyst, Risk Reviewer, Structural Analyst / Adversary, Research Lead).
 */
export function normalizeAIProvider(nameOrRole: string | null | undefined): AICouncilProvider {
  if (!nameOrRole) return 'unknown';
  const s = String(nameOrRole).trim().toLowerCase();
  if (!s) return 'unknown';

  // OpenAI / ChatGPT
  if (
    s === 'gpt' || s.startsWith('gpt-') || s.startsWith('gpt ') ||
    s === 'chatgpt' || s.includes('chatgpt') ||
    s === 'openai' || s.includes('openai') ||
    s === 'market analyst' || s === 'intelligence' ||
    s === 'o1' || s === 'o3' || s === 'o4'
  ) {
    return 'chatgpt';
  }

  // Anthropic / Claude
  if (
    s === 'claude' || s.startsWith('claude-') || s.startsWith('claude ') ||
    s.includes('anthropic') ||
    s === 'risk reviewer' || s === 'risk challenger' || s === 'quantitative analyst' ||
    s === 'reasoning' || s === 'wisdom'
  ) {
    return 'claude';
  }

  // Google / Gemini
  if (
    s === 'gemini' || s.startsWith('gemini-') || s.startsWith('gemini ') ||
    s === 'google' || (s.includes('google') && !s.includes('perplexity')) ||
    s === 'structural analyst' || s === 'adversary' ||
    s === 'knowledge' || s === 'memory'
  ) {
    return 'gemini';
  }

  // Perplexity
  if (
    s === 'perplexity' || s.includes('perplexity') ||
    s === 'research lead' || s === 'research analyst' ||
    s === 'discovery' || s === 'search'
  ) {
    return 'perplexity';
  }

  return 'unknown';
}

/** Return up-to-two-letter initials for an unknown provider as a fallback glyph. */
function initials(name: string | null | undefined): string {
  if (!name) return 'AI';
  const cleaned = String(name).trim();
  if (!cleaned) return 'AI';
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Resolve a provider/model/role string to its branded AI Council icon.
 *
 * For known providers, returns the PNG asset path + alt text. For unknown
 * providers, returns `src: null` plus initials in `alt`/`label` so the caller
 * can render a text-fallback badge instead of a broken image.
 */
export function getAIProviderIcon(nameOrRole: string | null | undefined): AIProviderIcon {
  const provider = normalizeAIProvider(nameOrRole);
  if (provider === 'unknown') {
    const init = initials(nameOrRole);
    return { src: null, alt: init, label: nameOrRole ? String(nameOrRole) : 'AI', provider };
  }
  return {
    src: ICON_PATH[provider],
    alt: ICON_ALT[provider],
    label: ICON_LABEL[provider],
    provider,
  };
}
